import { view } from '@risingstack/react-easy-state'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import PropertyMap from '../../../components/property-map/property-map'
import GenericPageContainer from '../../../GenericPageElements/GenericPageContainer'
import request from '../../../helpers/request'
import hasPermission from '../../../helpers/permissions'
import endpoints from '../../../helpers/endpoints'
import { GenericTable } from '../../../components/ap-generics/components/GenericTable.jsx'
import { PostcodeSearchModal } from '../../../components/ap-generics/components/modals/PostcodeSearchModal.jsx'
import classCodes from '../../../assets/classification-codes.json'
import syncRowHeights  from '../../../helpers/syncRowHeights.js'
import { CMOSLive } from './shared.jsx'

const initialEntityState = {
  data: [],
  index: 0,
  customPostcode: '',
  loading: false,
  postcodeModal: ''
}

export default view(function MoslDqaSearch() {
  const [summaryData, setSummaryData] = useState({})
  const [wsOutcome, setWsOutcome] = useState('')
  const [search, setSearch] = useState('')
  const [spid, setSpid] = useState('')
  const [loading, setLoading] = useState(false)
  const [translations, setTranslations] = useState({})
  const [entities, setEntities] = useState({
    addresses: { ...initialEntityState },
    voas: { ...initialEntityState },
    cts: { ...initialEntityState }
  })
  const prefix = 'MOSL_DQA'

  useEffect(() => {
    request(true).get('/translate?key=mosl_eligibility')
      .then(e => setTranslations(e.data))
  }, [])

  const getSummaryData = useCallback(() => {
    setSearch('')
    setSummaryData({})
    setWsOutcome('')
    setLoading(true)
    setEntities({
      addresses: { ...initialEntityState },
      voas: { ...initialEntityState },
      cts: { ...initialEntityState }
    })
    request(true).get(endpoints[`${prefix}_SEARCH_BY_SPID`], {
      params: { spid }
    })
      .then(e => {
        const cust = e.data.customer || {}
        setSummaryData(cust)
        setWsOutcome(cust.WS_Outcome)
        setSpid(cust.Core_SPID)
        setLoading(false)
      })
      .catch(() => {
        setSummaryData({})
        setWsOutcome('')
        window.alert('Could not find SPID.')
        setLoading(false)
      })
  }, [spid])

  useEffect(() => {
    if (spid && spid !== summaryData.Core_SPID) {
      getSummaryData()
    }
  }, [spid])

  const getEntityData = useCallback((entityType, endpointKey, fallbackField) => {
    const postcode = entities[entityType].customPostcode ||
      summaryData.ABP_Postcode ||
      summaryData[fallbackField] ||
      summaryData.CL_Postcode ||
      summaryData.CMOS_Meter_Address_Postcode

    if (!postcode) {
      setEntities(prev => ({ ...prev, [entityType]: { ...prev[entityType], loading: false } }))
      return
    }
    setEntities(prev => ({ ...prev, [entityType]: { ...prev[entityType], data: [], index: 0, loading: true } }))
    request(true).get(endpoints[`${prefix}_${endpointKey}`], {
      params: { postcode, spid: summaryData.Core_SPID }
    })
      .then(e => {
        let _data = e.data
        if (entityType === 'addresses') {
          _data = _data.map(item => {
            if (item.CHANGE_CODE) item.CHANGE_CODE = item.CHANGE_CODE === 'D' ? 'Deleted' : 'Active'
            if (item.CLASSIFICATION_CODE) {
              const found = classCodes.find(cc => cc.Concatenated === item.CLASSIFICATION_CODE)
              item.CLASSIFICATION_CODE = found && found.Class_Desc
            }
            return item
          })
        } else if (entityType === 'cts') {
          _data = _data.map(item => {
            const addressLines = item.address.split(', ')
            addressLines.forEach((line, i) => {
              if (line !== item.postcode) {
                item['CT_Address_Line_' + (i + 1)] = line
              }
            })
            item.council_tax_band = item.council_tax_band === 'Deleted' ? '' : item.council_tax_band
            item.ct_status = item.council_tax_band === '' ? 'Deleted' : 'Active'
            return item
          })
        } else if (entityType === 'voas') {
          _data = _data.map(item => {
            if (item.Effective_Status && item.Effective_Status === 'DELETED') {
              item.Deletion_Date = item.Current_From_Date
            }
            return item
          });
        }
        let displayFirst = 0
        if (summaryData.CF_UPRN || summaryData.Matched_UPRN || summaryData.CMOS_UPRN) {
          if (entityType === 'cts') {
            displayFirst = _data.findIndex(item => item.au_reference_number === summaryData.au_reference_number)
          } else if (entityType === 'voas') {
            displayFirst = _data.findIndex(item => item.BA_Reference_Number === ( (summaryData.CF_VOA_BA_Reference && summaryData.Postcode === summaryData.CF_Postcode) || (summaryData.Matched_VOA_No) || (summaryData.CMOS_VOA_No)  ))
          } else {
            displayFirst = _data.findIndex(item =>
              item.UPRN === parseInt(
                [summaryData.CF_UPRN, summaryData.Matched_UPRN, summaryData.CMOS_UPRN]
                  .find(value => value)?.trim()
              )
            )
          }
        }
        setEntities(prev => ({
          ...prev,
          [entityType]: { ...prev[entityType], data: _data, index: displayFirst !== -1 ? displayFirst : 0, loading: false }
        }))
      })
      .catch(() => {
        window.alert(`Could not get ${entityType} data.`)
        setEntities(prev => ({ ...prev, [entityType]: { ...prev[entityType], data: [], loading: false } }))
      })
  }, [entities, summaryData, prefix])

  useEffect(() => {
    if (summaryData.Core_SPID) {
      getEntityData('addresses', 'GET_ADDRESSES', 'CL_Postcode')
      getEntityData('voas', 'GET_VOAS', 'VO_Postcode')
      getEntityData('cts', 'GET_CT', 'CL_Postcode')
    }
  }, [summaryData.Core_SPID])

  const cycleIndex = useCallback((entityType, change) => {
    setEntities(prev => {
      const current = prev[entityType]
      const newIndex = current.index + change
      if (newIndex < 0 || newIndex >= current.data.length) return prev
      return { ...prev, [entityType]: { ...current, index: newIndex } }
    })
  }, [])

  const restorePostcode = useCallback(entityType => {
    setEntities(prev => ({ ...prev, [entityType]: { ...prev[entityType], customPostcode: '' } }))
  }, [])

  useEffect(() => {
    if (entities.addresses.customPostcode === "") {
      getEntityData('addresses', 'GET_ADDRESSES', 'CL_Postcode')
    }
  }, [entities.addresses.customPostcode])

  useEffect(() => {
    if (entities.voas.customPostcode === "") {
      getEntityData('voas', 'GET_VOAS', 'VO_Postcode')
    }
  }, [entities.voas.customPostcode])

  useEffect(() => {
    if (entities.cts.customPostcode === "") {
      getEntityData('cts', 'GET_CT', 'CL_Postcode')
    }
  }, [entities.cts.customPostcode])

  const abpRows = useMemo(() => [
    [translations['UPRN'], 'UPRN'],
    [translations['STATUS'], 'STATUS'],
    [translations['CLASSIFICATION_CODE'], 'CLASSIFICATION_CODE'],
    [translations['ORGANISATION'], 'ORGANISATION'],
    [translations['SUB_BUILDING'], 'SUB_BUILDING'],
    [translations['BUILDING_NAME'], 'BUILDING_NAME'],
    [translations['BUILDING_NUMBER'], 'BUILDING_NUMBER'],
    [translations['STREET_NAME'], 'STREET_NAME'],
    [translations['LOCALITY'], 'LOCALITY'],
    [translations['TOWN_NAME'], 'TOWN_NAME'],
    [translations['POST_TOWN'], 'POST_TOWN'],
    [translations['Postcode'], 'POSTCODE']
  ], [translations])

  const voaRows = useMemo(() => [
    [translations['BA_Reference_Number'], 'BA_Reference_Number'],
    [translations['Effective_Status'], 'Effective_Status'],
    [translations['Deletion_Date'], 'Deletion_Date'],
    [translations['ORGANISATION'], 'Firms_Name'],
    [translations['VOA_Description'], 'Primary_Description_Text'],
    [translations['Number_Or_Name'], 'Number_Or_Name'],
    [translations['Street'], 'Street'],
    [translations['Sub_Street_level_1'], 'Sub_Street_Level_1'],
    [translations['Town'], 'Town'],
    [translations['Postal_District'], 'Postal_District'],
    [translations['County'], 'County'],
    [translations['Postcode'], 'Postcode'],
  ], [translations])

  const ctRows = useMemo(() => [
    [translations['CT_Reference'], 'au_reference_number'],
    [translations['CT_Band'], 'council_tax_band'],
    [translations['CT_Mixed_Use_Property'], 'mixed_use_property'],
    [translations['CF_Free_Descriptor'], 'CT_Free_Descriptor'],
    [translations['CF_Secondary_Addressable_Object'], 'CT_Secondary_Addressable_Object'],
    [translations['CF_Primary_Addressable_Object'], 'CT_Primary_Addressable_Object'],
    [translations['CF_Address_Line_1'], 'CT_Address_Line_1'],
    [translations['CF_Address_Line_2'], 'CT_Address_Line_2'],
    [translations['CF_Address_Line_3'], 'CT_Address_Line_3'],
    [translations['CF_Address_Line_4'], 'CT_Address_Line_4'],
    [translations['CF_Address_Line_5'], 'CT_Address_Line_5'],
    [translations['CF_Postcode'], 'postcode']
  ], [translations])

    useEffect(() => {
      syncRowHeights()
    }, [translations, summaryData, entities])

    useEffect(() => {
      if (!wsOutcome || wsOutcome === summaryData.WS_Outcome) return
      request(true).post(endpoints[`${prefix}_UPDATE_OUTCOMES`], {
        SPID: spid,
        WS_Outcome: wsOutcome,
      }).then(e => {
        // window.alert(e.data);
      }).catch(e => {
        console.log(e)
        window.alert("Update failed.")
      })
    }, [wsOutcome])

  return (
    <GenericPageContainer
      containerWidth="95vw"
      title="MOSL: DQA - Search"
      titleIconClass="fak fa-id-business-icon fa-2x colour-primary"
      titleRight={<Link className="navigate" to="/portal/mosl-dqa">To Summary</Link>}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'grid', gap: 10, gridTemplateColumns: '2fr 1fr' }}>
          <table className="table borders squish smaller-text left scale-text" style={{ tableLayout: 'fixed', width: '100%' }}>
            <col width={"15%"} />
            <thead>
              <tr>
                <th colSpan={8}>{translations['Header_Summary']}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td width={400}>{translations['Core_SPID']}</td>
                <td colSpan={4}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <input type="text" style={{ border: 'none' }} onChange={e => setSearch(e.target.value)} value={search || spid} onKeyDown={e => { if (e.key === 'Enter') setSpid(search) }} />
                    {loading && <i className="fa fa-spinner fa-spin" />}
                    <span className="fas fa-search colour-primary" style={{ cursor: 'pointer' }} onClick={() => setSpid(search)}></span>
                  </div>
                </td>
              </tr>
              <tr>
                <td>{translations['Address_DQA']}</td>
                <td>{summaryData.DQA_Address}</td>
                <td>{translations['UPRN_DQA']}</td>
                <td>{summaryData.DQA_UPRN}</td>
                <td>{translations['VOA_DQA']}</td>
                <td>{summaryData.DQA_VOA}</td>
                <td>{translations['Premise_DQA']}</td>
                <td>{summaryData.Premise_Assured}</td>
              </tr>
              <tr>
                <td>{translations['Premises_Assurance']}</td>
                <td>{summaryData.Address_holistic_assured}</td>
                <td>{translations['UPRN_Assurance']}</td>
                <td>{summaryData.UPRN_holistic_assured}</td>
                <td>{translations['VOA_Assurance']}</td>
                <td>{summaryData.VOA_holistic_assured}</td>
                <td>{translations['Occupancy_Status']}</td>
                <td>{summaryData.Occupancy_Status}</td>    
              </tr>
              <tr>
                <td>{translations['CMOS_UPRN']}</td>
                <td>{summaryData.CMOS_UPRN}</td>
                <td>{translations['Matched_UPRN']}</td>
                <td>{summaryData.Matched_UPRN}</td>
                <td>{translations['UPRN_Reason_Code']}</td>
                <td>{summaryData.UPRN_Reason_code}</td>
                <td>{translations['Status_UPRN']}</td>
                <td>{summaryData.UPRN_Status}</td>
              </tr>
              <tr>
                <td>{translations['CMOS_VOA_No']}</td>
                <td>{summaryData.CMOS_VOA_No}</td>
                <td>{translations['Matched_VOA_No']}</td>
                <td>{summaryData.Matched_VOA_No}</td>
                <td>{translations['VOA_Reason_Code']}</td>
                <td>{summaryData.VOA_Reason_code}</td>
                <td>{translations['Status_VOA_No']}</td>
                <td>{summaryData.VOA_BA_Status}</td>
              </tr>
              <tr>
                <td>{translations['CMOS_Address_Single_Line']}</td>
                <td colSpan="7">{summaryData.CMOS_Address} {summaryData.CMOS_Postcode}</td>
              </tr>
              <tr>
                <td>{translations['CMOS_UPRN_Single_Line']}</td>
                <td colSpan="7">{summaryData.UPRN_Lookup_Address} {summaryData.UPRN_Lookup_Postcode}</td>
              </tr>
              <tr>
                <td>{translations['Matched_UPRN_Address']}</td>
                <td colSpan="7">{summaryData.ABP_Matched_Address} {summaryData.ABP_Matched_Postcode}</td>
              </tr>
              <tr>
                <td>{translations['CMOS_VOA_Single_Line']}</td>
                <td colSpan="7">{summaryData.VOA_BA_Lookup_Address} {summaryData.VOA_BA_Lookup_Postcode}</td>
              </tr>
              <tr>
                <td>{translations['Matched_VOA_Single_Line']}</td>
                <td colSpan="7">{summaryData.VOA_Matched_Address} {summaryData.VOA_Matched_Postcode}</td>
              </tr>
            </tbody>
          </table>
          <PropertyMap
            translations={translations}
            eastingNorthing={
              summaryData.CMOS_UPRN_GISX && summaryData.CMOS_UPRN_GISY
                ? [parseInt(summaryData.CMOS_UPRN_GISX), parseInt(summaryData.CMOS_UPRN_GISY)]
                : null
            }
            address={(summaryData.CMOS_Address + summaryData.CMOS_Postcode) || summaryData.Meter_Address_Single_Line}
          />
        </div>
        <div style={{ display: 'grid', gridAutoColumns: '1fr', gridAutoFlow: 'column', gap: 10 }}>
          <CMOSLive translations={translations} summaryData={summaryData} />
          <GenericTable title={translations['Header_AddressBase']}
            rows={abpRows}
            data={entities.addresses.data}
            loading={entities.addresses.loading}
            restorePostcode={() => restorePostcode('addresses')}
            index={entities.addresses.index}
            cycleIndex={change => cycleIndex('addresses', change)}
            customPostcode={entities.addresses.customPostcode}
            searchEnabled
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, addresses: { ...prev.addresses, postcodeModal: modal } }))}
          />
          <PostcodeSearchModal
            postcodeModal={entities.addresses.postcodeModal}
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, addresses: { ...prev.addresses, postcodeModal: modal } }))}
            customPostcode={entities.addresses.customPostcode}
            setCustomPostcode={val => setEntities(prev => ({ ...prev, addresses: { ...prev.addresses, customPostcode: val } }))}
            setLoading={val => setEntities(prev => ({ ...prev, addresses: { ...prev.addresses, loading: val } }))}
            getData={() => getEntityData('addresses', 'GET_ADDRESSES', 'CL_Postcode')}
          />
          <GenericTable title={translations['Header_Valuation']}
            rows={voaRows}
            data={entities.voas.data}
            loading={entities.voas.loading}
            restorePostcode={() => restorePostcode('voas')}
            index={entities.voas.index}
            cycleIndex={change => cycleIndex('voas', change)}
            customPostcode={entities.voas.customPostcode}
            searchEnabled
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, voas: { ...prev.voas, postcodeModal: modal } }))}
          />
          <PostcodeSearchModal
            postcodeModal={entities.voas.postcodeModal}
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, voas: { ...prev.voas, postcodeModal: modal } }))}
            customPostcode={entities.voas.customPostcode}
            setCustomPostcode={val => setEntities(prev => ({ ...prev, voas: { ...prev.voas, customPostcode: val } }))}
            setLoading={val => setEntities(prev => ({ ...prev, voas: { ...prev.voas, loading: val } }))}
            getData={() => getEntityData('voas', 'GET_VOAS', 'VO_Postcode')}
          />
          <GenericTable title={translations['Council_Tax']}
            rows={ctRows}
            data={entities.cts.data}
            loading={entities.cts.loading}
            restorePostcode={() => restorePostcode('cts')}
            index={entities.cts.index}
            cycleIndex={change => cycleIndex('cts', change)}
            customPostcode={entities.cts.customPostcode}
            searchEnabled
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, cts: { ...prev.cts, postcodeModal: modal } }))}
          />
          <PostcodeSearchModal
            postcodeModal={entities.cts.postcodeModal}
            setPostcodeModal={modal => setEntities(prev => ({ ...prev, cts: { ...prev.cts, postcodeModal: modal } }))}
            customPostcode={entities.cts.customPostcode}
            setCustomPostcode={val => setEntities(prev => ({ ...prev, cts: { ...prev.cts, customPostcode: val } }))}
            setLoading={val => setEntities(prev => ({ ...prev, cts: { ...prev.cts, loading: val } }))}
            getData={() => getEntityData('cts', 'GET_CT', 'CL_Postcode')}
          />
        </div>
      </div>
    </GenericPageContainer>
  )
})
